import Profile from "assets/Profile_pic.png";
import { useState } from "react";
import Select, { MultiValue, ActionMeta, components, SingleValue, OptionProps } from "react-select";
import { useTranslation } from "react-i18next";
import { Box, InputLabel, Typography } from "@mui/material";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import ArrowDropUpIcon from "@mui/icons-material/ArrowDropUp";

type UserOption = { label: string; value: string; icon: string | null };

interface ICustomSearchSelectProps {
  options: UserOption[];
  onChange: (selected: UserOption[] | null) => void;
}

const CustomSearchSelect = ({ options, onChange }: ICustomSearchSelectProps) => {
  const { t } = useTranslation();
  const [selectedOptions, setSelectedOptions] = useState<UserOption[] | UserOption | null>(null);

  //*TODO --- need to be updated for all users or users who have not logged into the service for a long time
  // const finalOptions: UserOption[] = [{ label: t("allUsers"), value: "all", icon: null }, ...options];
  const finalOptions: UserOption[] = [...options];

  const isAllSelected = (): boolean => {
    if (!selectedOptions) return false;
    if (!Array.isArray(selectedOptions)) {
      return selectedOptions.value === "all";
    }
    return selectedOptions.some((option) => option.value === "all");
  };

  const handleChange = (
    selected: MultiValue<UserOption> | SingleValue<UserOption> | null,
    _actionMeta: ActionMeta<UserOption>
  ) => {
    if (selected === null) {
      setSelectedOptions(null);
      onChange(null);
      return;
    }

    if (Array.isArray(selected)) {
      const foundAll = selected.find((option) => option.value === "all");
      if (foundAll) {
        setSelectedOptions(foundAll);
        onChange([foundAll]);
        return;
      }
      setSelectedOptions(selected);
      onChange(selected);
      return;
    }

    const single = selected as UserOption;
    if (single.value === "all") {
      setSelectedOptions(single);
      onChange([single]);
    } else {
      setSelectedOptions([single]);
      onChange([single]);
    }
  };

  const CustomOption = (props: OptionProps<UserOption, boolean>) => {
    const { label, value, icon } = props.data;
    return (
      <components.Option {...props}>
        <Box sx={{ display: "flex", alignItems: "center", gap: "10px" }}>
          {value !== "all" && (
            <img src={icon || Profile} alt={`${label} icon`} style={{ width: 20, height: 20, borderRadius: "50%" }} />
          )}
          <Typography
            sx={{
              marginLeft: value === "all" ? "28px" : 0,
              fontSize: "14px",
              fontWeight: value === "all" ? 500 : 400,
              lineHeight: 1.43,
              color: "#2E263D",
            }}
          >
            {label}
          </Typography>
        </Box>
      </components.Option>
    );
  };

  const CustomSingleValue = (props: any) => {
    const { label, value, icon } = props.data;
    return (
      <Box sx={{ display: "flex", alignItems: "center", gap: "6px" }}>
        {value !== "all" && (
          <img src={icon || Profile} alt={`${label} icon`} style={{ width: 20, height: 20, borderRadius: "50%" }} />
        )}
        <Typography sx={{ fontSize: "14px", lineHeight: 1.43, color: "#2E263D" }}>{label}</Typography>
      </Box>
    );
  };

  const CustomMultiValue = (props: any) => {
    const { label, value, icon } = props.data;
    return (
      <Box sx={{ display: "flex", alignItems: "center", gap: "6px" }}>
        {value !== "all" && (
          <img src={icon || Profile} alt={`${label} icon`} style={{ width: 20, height: 20, borderRadius: "50%" }} />
        )}
        <Typography sx={{ fontSize: "14px", lineHeight: 1.43, color: "#2E263D" }}>{label}</Typography>
        <Typography
          component="span"
          onClick={() => props.removeProps.onClick()}
          sx={{ width: 20, textAlign: "center", cursor: "pointer" }}
        >
          ×
        </Typography>
      </Box>
    );
  };

  const CustomDropdownIndicator = (props: any) => {
    const { menuIsOpen } = props.selectProps;
    return (
      <components.DropdownIndicator {...props}>
        {menuIsOpen ? (
          <ArrowDropUpIcon sx={{ width: 24, color: "#6D6777" }} />
        ) : (
          <ArrowDropDownIcon sx={{ width: 24, color: "#6D6777" }} />
        )}
      </components.DropdownIndicator>
    );
  };

  const isAllUsersOptionSelected = isAllSelected();

  return (
    <Box sx={{ width: "100%" }}>
      <InputLabel
        htmlFor="user-select"
        sx={{
          fontFamily: "var(--cygreMedium), sans-serif",
          color: "#2E263D",
          fontSize: "12px",
          lineHeight: 1.22,
          textAlign: "left",
          mb: "10px",
        }}
      >
        {t("users")}
      </InputLabel>

      <Select
        id="user-select"
        options={finalOptions}
        isMulti={!isAllUsersOptionSelected}
        value={selectedOptions as any}
        onChange={handleChange as any}
        placeholder={t("selectUsers")}
        components={{
          Option: CustomOption,
          SingleValue: CustomSingleValue,
          MultiValue: CustomMultiValue,
          DropdownIndicator: CustomDropdownIndicator,
        }}
        styles={{
          container: (base) => ({
            ...base,
            width: "100%",
            backgroundColor: "#F6F7FB !important",
            borderRadius: "8px",
            zIndex: 100,
          }),
          control: (base) => ({
            ...base,
            fontFamily: "var(--cygreRegular), sans-serif",
            fontSize: "14px",
            lineHeight: 1.43,
            color: "#2E263D",
            backgroundColor: "#F6F7FB !important",
            borderRadius: "8px",
            border: "none",
            boxShadow: "none",
            cursor: "pointer",
          }),
          multiValue: (base) => ({
            ...base,
            display: "flex",
            alignItems: "center",
            padding: "5px",
            backgroundColor: "#F6F7FB !important",
            border: "none",
            borderRadius: "4px",
            margin: "2px",
            flexShrink: 0,
          }),
          multiValueLabel: (base) => ({
            ...base,
            display: "flex",
            alignItems: "center",
            backgroundColor: "#F6F7FB !important",
          }),
          multiValueRemove: (base) => ({
            ...base,
            cursor: "pointer",
          }),
          indicatorsContainer: (base) => ({
            ...base,
            color: "#6D6777",
          }),
          valueContainer: (base) => ({
            ...base,
            display: "flex",
            gap: "4px",
            padding: "0px 13px",
            fontFamily: "var(--cygreRegular), sans-serif",
            fontSize: "14px",
            lineHeight: 1.43,
            color: "#2E263D",
          }),
          option: (base, state) => ({
            ...base,
            fontSize: "14px",
            lineHeight: 1.43,
            backgroundColor: state.isFocused ? "#f6f9fc" : state.isSelected ? "#FFF4ED" : "transparent",
            fontFamily: "var(--cygreRegular), sans-serif",
            color: "#2E263D",
            cursor: "pointer",
          }),
          placeholder: (base) => ({
            ...base,
            fontSize: "14px",
            lineHeight: 1.43,
            fontFamily: "var(--cygreRegular), sans-serif",
            textOverflow: "ellipsis !important",
            color: "#b4b1b9",
            opacity: 1,
          }),
        }}
      />
    </Box>
  );
};

export default CustomSearchSelect;
